import React, { useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import DynamicTitle from "./DynamicTitle";
import { trackEvent } from "../utils/posthog";
import { trackSubscribe } from "../utils/metaPixel";

function SubscriptionSuccess() {
  const location = useLocation();
  const hasTracked = useRef(false);

  // Track payment completed and subscription_success_viewed on mount
  useEffect(() => {
    if (!hasTracked.current) {
      hasTracked.current = true;
      const source = document.referrer || 'direct';
      trackEvent('payment_completed', { source });
      trackEvent('subscription_success_viewed', {
        plan: 'unknown',
        source,
      });
    }
  }, []);

  // Meta: Subscribe event when user lands on success page
  useEffect(() => {
    trackSubscribe({ value: 0, currency: "USD" });
  }, []);

  return (
    <div className="flex flex-col min-h-screen">
      <DynamicTitle>Subscription Successful</DynamicTitle>
      <Header />

      <main className="flex-grow">
        <section className="bg-surfaceTint py-16">
          <div className="max-w-4xl mx-auto px-4">
            {/* Success Message */}
            <div className="bg-white rounded-lg border border-tint p-8 mb-8 text-center">
              <div className="text-6xl mb-4">✅</div>
              <h1 className="text-3xl md:text-4xl font-bold text-text mb-4">
                Thank You for Subscribing!
              </h1>
              <p className="text-lg text-textSecondary mb-6">
                Your subscription has been successfully processed.
              </p>
            </div>

            {/* Next Steps Guide */}
            <div className="bg-white rounded-lg border border-tint p-8">
              <h2 className="text-2xl font-bold text-text mb-6">
                What's Next?
              </h2>
              
              <div className="space-y-6">
                {/* Step 1 */}
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-10 h-10 bg-primary text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                    1
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-text mb-2">
                      Check Your Email
                    </h3>
                    <p className="text-textSecondary mb-2">
                      We've sent you a magic link to the email address you used during checkout. 
                      This secure link will allow you to log in without a password.
                    </p>
                    <p className="text-sm text-textSecondary">
                      <strong>Tip:</strong> Check your spam folder if you don't see it within a few minutes.
                    </p>
                  </div>
                </div>

                {/* Step 2 */}
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-10 h-10 bg-primary text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                    2
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-text mb-2">
                      Click the Magic Link
                    </h3>
                    <p className="text-textSecondary mb-2">
                      Click the link in your email to securely log in to your account. 
                      No password needed!
                    </p>
                  </div>
                </div>

                {/* Step 3 */}
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-10 h-10 bg-primary text-white rounded-full flex items-center justify-center font-bold text-lg mr-4">
                    3
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-text mb-2">
                      Start Your First Session
                    </h3>
                    <p className="text-textSecondary mb-2">
                      Once logged in, you'll be able to access all our bilateral stimulation sessions. 
                      We recommend starting with our guided tour to get familiar with the platform.
                    </p>
                  </div>
                </div>
              </div>

              {/* Alternative Login Option */}
              <div className="mt-8 p-6 bg-background rounded-lg border border-tint">
                <h3 className="text-lg font-semibold text-text mb-2">
                  Already have an account?
                </h3>
                <p className="text-textSecondary mb-4">
                  If you already have a RealignMind account, you can log in directly:
                </p>
                <Link
                  to="/login"
                  className="inline-block bg-accent hover:bg-accentDark text-white px-6 py-3 rounded-lg font-semibold transition-colors"
                >
                  Log In Now
                </Link>
              </div>

              {/* Help Section */}
              <div className="mt-8 pt-6 border-t border-tint">
                <h3 className="text-lg font-semibold text-text mb-2">
                  Need Help?
                </h3>
                <p className="text-textSecondary mb-4">
                  If you have any questions or need assistance, we're here to help.
                </p>
                <div className="flex flex-wrap gap-4">
                  <Link
                    to="/contact"
                    className="text-accent hover:underline"
                  >
                    Contact Support
                  </Link>
                  <Link
                    to="/how-it-works"
                    className="text-accent hover:underline"
                  >
                    Learn How It Works
                  </Link>
                  <Link
                    to="/demo"
                    state={{ from: location.pathname }}
                    className="text-accent hover:underline"
                  >
                    Try the Demo
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

export default SubscriptionSuccess;
